import { useState } from "react";
import { ReviewTileItem } from "@/types/tile.types";

export interface UseTileCommentOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

export const useTileComment = (options?: UseTileCommentOptions) => {
  /* global Word, setTimeout */
  const [isAddingComments, setIsAddingComments] = useState(false);
  const [addingCommentTileId, setAddingCommentTileId] = useState<string | null>(null);

  const formatCommentText = (tile: ReviewTileItem): string => {
    let text = "";
    const originalData = tile.originalData || {};

    if (tile.queryType) {
      text += `Type: ${tile.queryType}\n\n`;
    }

    if (tile.impact && tile.impact !== "ALL") {
      text += `Impact: ${tile.impact}\n\n`;
    }

    if (tile.clause) {
      text += `Clause: ${tile.clause}\n\n`;
    }

    // Add fields based on tile type
    if (originalData.ambiguousText) {
      text += `Ambiguous Text: ${originalData.ambiguousText}\n\n`;
    }
    if (originalData.complexText) {
      text += `Complex Text: ${originalData.complexText}\n\n`;
    }
    if (originalData.riskText) {
      text += `Risk Text: ${originalData.riskText}\n\n`;
    }
    if (originalData.nonStandardText) {
      text += `Non-Standard Text: ${originalData.nonStandardText}\n\n`;
    }
    if (originalData.text1) {
      text += `Text 1: ${originalData.text1}\n\n`;
    }
    if (originalData.text2) {
      text += `Text 2: ${originalData.text2}\n\n`;
    }

    if (tile.reason) {
      text += `Reason: ${tile.reason}\n\n`;
    }

    if (tile.analysis) {
      text += `Analysis: ${tile.analysis}\n\n`;
    }

    if (tile.explanation) {
      text += `Explanation: ${tile.explanation}\n\n`;
    }

    if (originalData.riskExplanation) {
      text += `Risk Explanation: ${originalData.riskExplanation}\n\n`;
    }

    const suggestedClause =
      originalData.suggestedclause ||
      originalData.suggestion ||
      originalData.suggestions ||
      originalData.suggestedNumber ||
      originalData.additionalClause ||
      originalData.missingClause ||
      null;

    if (suggestedClause) {
      text += `Suggestion: ${suggestedClause}\n\n`;
    }

    return text.trim();
  };

  const addSingleComment = async (tile: ReviewTileItem): Promise<boolean> => {
    setAddingCommentTileId(tile.id);

    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        const commentText = formatCommentText(tile);

        // Insert comment at current selection
        selection.insertComment(commentText);

        await context.sync();

        options?.onSuccess?.(`Comment added for "${tile.clause || tile.id}"`);
      });
      return true;
    } catch (error: any) {
      console.error("Error adding comment:", error);
      options?.onError?.(`Failed to add comment: ${error.message}`);
      return false;
    } finally {
      setAddingCommentTileId(null);
    }
  };

  const addMultipleComments = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (tiles.length === 0) {
      options?.onError?.("No tiles to add as comments");
      return false;
    }

    setIsAddingComments(true);
    let successCount = 0;

    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection();

        // For bulk comments, we'll add them as separate comments
        // or combine them into one comment depending on preference
        const combinedComment = tiles
          .map((tile, index) => `${index + 1}. ${formatCommentText(tile)}`)
          .join("\n");

        selection.insertComment(`Review Items:\n${combinedComment}`);

        await context.sync();

        successCount = tiles.length;
        options?.onSuccess?.(`${successCount} review items added as comments`);
      });
      return true;
    } catch (error: any) {
      console.error("Error adding bulk comments:", error);
      options?.onError?.(`Failed to add comments: ${error.message}`);
      return false;
    } finally {
      setIsAddingComments(false);
    }
  };

  const addCommentsAtSelection = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (tiles.length === 0) {
      options?.onError?.("No tiles to add as comments");
      return false;
    }

    setIsAddingComments(true);
    let successCount = 0;

    try {
      for (const tile of tiles) {
        await Word.run(async (context) => {
          const selection = context.document.getSelection();
          const commentText = formatCommentText(tile);

          selection.insertComment(commentText);

          await context.sync();
          successCount++;
        });

        // Small delay between comments to avoid overwhelming Word
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      options?.onSuccess?.(`${successCount} comments added successfully`);
      return true;
    } catch (error: any) {
      console.error("Error adding sequential comments:", error);
      options?.onError?.(`Failed to add comments. Added ${successCount} of ${tiles.length}`);
      return false;
    } finally {
      setIsAddingComments(false);
    }
  };

  return {
    isAddingComments,
    addingCommentTileId,
    addSingleComment,
    addMultipleComments,
    addCommentsAtSelection,
  };
};
